import { useSetMaintenanceMode } from "../../../errorHandling/useSetMaintenanceMode";

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

interface IEnvJSON {
  APP_ENV: string;
  APP_KEY_GA: string;
  APP_KEY_RECAPTCHA: string;
  /** EASY entry page - target of "Back to EASY" when a session handoff fails */
  APP_EASY_URL?: string;
}
interface IEnvContext {
  envJSON: IEnvJSON | null;
  isLoadingEnv: boolean;
}

interface IEnvJSONProvider {
  children: ReactNode;
}

export const EnvContext = createContext<IEnvContext | undefined>(undefined);

const RETRY_INTERVAL_MS = 60000;

export const EnvJSONProvider = ({ children }: IEnvJSONProvider): ReactNode => {
  const [envJSON, setEnvJSON] = useState<IEnvJSON | null>(null);
  const [isLoadingEnv, setIsLoadingEnv] = useState<boolean>(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const updateMaintenanceMode = useSetMaintenanceMode();

  const loadEnv = useEffectEvent(async (): Promise<void> => {
    try {
      const res = await fetch("/env.json");
      if (res.status === 503) {
        updateMaintenanceMode(true);
        return;
      }
      if (!res.ok) {
        throw new Error(`Failed to load env.json (status ${res.status})`);
      }
      const envData = await res.json();
      updateMaintenanceMode(false);
      setEnvJSON(envData);
      setIsLoadingEnv(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (error) {
      console.error("Failed to load env.json, retrying...", error);
    }
  });

  useEffect(() => {
    if (envJSON) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadEnv();
    intervalRef.current = setInterval(() => {
      loadEnv();
    }, RETRY_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [envJSON]);

  return (
    <EnvContext.Provider value={{ envJSON, isLoadingEnv: isLoadingEnv }}>
      {children}
    </EnvContext.Provider>
  );
};

export const useEnvJSON = (): IEnvContext => {
  const context = useContext(EnvContext);
  if (context === undefined) {
    throw new Error("'useEnvJSON' must be used within a 'EnvJSONProvider'.");
  }

  return context;
};
